/**
 * Copyright (c) 2024–2025, Daily
 *
 * SPDX-License-Identifier: BSD 2-Clause License
 */
/**
 * Pipecat Client Implementation
 *
 * This client connects to an RTVI-compatible bot server using WebSocket.
 *
 * Requirements:
 * - A running RTVI bot server (defaults to http://localhost:7860)
 */
import { PipecatClient, RTVIEvent, } from '@pipecat-ai/client-js';
import { WebSocketTransport } from '@pipecat-ai/websocket-transport';
import { authService } from './auth-service';
export class WebsocketClientApp {
    constructor(callbacks) {
        this.pcClient = null;
        this.callbacks = {};
        console.log('WebsocketClientApp');
        this.callbacks = callbacks || {};
        // Create audio element for WebRTC transport (MediaStreamTrack-based)
        // For WebSocket transport, audio is handled internally by WavStreamPlayer
        this.botAudio = document.createElement('audio');
        this.botAudio.autoplay = true;
        document.body.appendChild(this.botAudio);
    }
    /**
     * Set callbacks for voice transcripts (can be called after construction)
     */
    setCallbacks(callbacks) {
        this.callbacks = { ...this.callbacks, ...callbacks };
    }
    /**
     * Check for available media tracks and set them up if present
     */
    setupMediaTracks() {
        if (!this.pcClient)
            return;
        const tracks = this.pcClient.tracks();
        if (tracks.bot?.audio) {
            this.setupAudioTrack(tracks.bot.audio);
        }
    }
    /**
     * Set up listeners for track events (start/stop)
     */
    setupTrackListeners() {
        if (!this.pcClient)
            return;
        this.pcClient.on(RTVIEvent.TrackStarted, (track, participant) => {
            if (!participant?.local && track.kind === 'audio') {
                this.setupAudioTrack(track);
            }
        });
        this.pcClient.on(RTVIEvent.TrackStopped, (track, participant) => {
            console.log(`Track stopped: ${track.kind} from ${participant?.name || 'unknown'}`);
        });
    }
    /**
     * Set up an audio track for playback (for WebRTC transports)
     */
    setupAudioTrack(track) {
        console.log('Setting up audio track');
        if (this.botAudio.srcObject && 'getAudioTracks' in this.botAudio.srcObject) {
            const oldTrack = this.botAudio.srcObject.getAudioTracks()[0];
            if (oldTrack?.id === track.id)
                return;
        }
        this.botAudio.srcObject = new MediaStream([track]);
    }
    /**
     * Initialize and connect to the bot
     */
    async connect() {
        try {
            const startTime = Date.now();
            const PipecatConfig = {
                transport: new WebSocketTransport(),
                enableMic: true,
                enableCam: false,
                callbacks: {
                    onConnected: () => {
                        console.log('Connected');
                        this.callbacks.onConnected?.();
                    },
                    onDisconnected: () => {
                        console.log('Disconnected');
                        this.callbacks.onDisconnected?.();
                    },
                    onBotReady: (data) => {
                        console.log(`Bot ready: ${JSON.stringify(data)}`);
                        this.setupMediaTracks();
                        this.callbacks.onBotReady?.();
                    },
                    onUserTranscript: (data) => {
                        console.log(`User: ${data.text} (final: ${data.final})`);
                        this.callbacks.onUserTranscript?.(data.text, data.final);
                    },
                    onBotTranscript: (data) => {
                        console.log(`Bot: ${data.text}`);
                        this.callbacks.onBotTranscript?.(data.text);
                    },
                    onMessageError: (error) => {
                        console.error('Message error:', error);
                        this.callbacks.onError?.(String(error));
                    },
                    onError: (error) => {
                        console.error('Error:', error);
                        this.callbacks.onError?.(String(error));
                    },
                },
            };
            this.pcClient = new PipecatClient(PipecatConfig);
            // @ts-ignore - expose for debugging
            window.pcClient = this.pcClient;
            this.setupTrackListeners();
            console.log('Initializing devices...');
            await this.pcClient.initDevices();
            console.log('Connecting to bot...');
            // Get Firebase token for authentication
            const token = await authService.getIdToken();
            if (!token) {
                console.log('Warning: No authentication token - connecting without auth');
            }
            // Build custom headers with Firebase token
            const customHeaders = new Headers();
            if (token) {
                customHeaders.set('Authorization', `Bearer ${token}`);
            }
            await this.pcClient.startBotAndConnect({
                endpoint: '/connect',
                headers: token ? customHeaders : undefined,
            });
            const timeTaken = Date.now() - startTime;
            console.log(`Connection complete, timeTaken: ${timeTaken}`);
        }
        catch (error) {
            console.error('Full error object:', error);
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error(`Error connecting: ${errorMessage}`);
            this.callbacks.onError?.(errorMessage);
            if (this.pcClient) {
                try {
                    await this.pcClient.disconnect();
                }
                catch (disconnectError) {
                    console.error('Disconnect error:', disconnectError);
                }
            }
        }
    }
    /**
     * Disconnect from the bot and clean up media resources
     */
    async disconnect() {
        if (this.pcClient) {
            try {
                await this.pcClient.disconnect();
                this.pcClient = null;
                if (this.botAudio.srcObject && 'getAudioTracks' in this.botAudio.srcObject) {
                    this.botAudio.srcObject.getAudioTracks().forEach((track) => track.stop());
                    this.botAudio.srcObject = null;
                }
            }
            catch (error) {
                console.error(`Error disconnecting: ${error.message}`);
            }
        }
    }
}
window.WebsocketClientApp = WebsocketClientApp;
